import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import isNumber from 'lodash/isNumber';

import constants from '@/app/dashboards/constants';
import { getReportConfigProp, REPORTS } from '@/app/dashboards/utils/reports.enum';

import type {
  AddReportOptions,
  GetHeightConfigFn,
  HeightConfig,
  LayoutItem,
  LayoutPosition,
  LayoutReportInfo,
  LayoutValidationResult,
  NormalizedReportInfo,
} from './layout.types';

/**
 * V1LayoutAdapter - Adapter for V1 (grid-based) dashboard layouts
 *
 * V1 layouts are flat arrays with grid coordinates:
 * [{ x, y, w, h, type, report_id, ... }]
 *
 * Movement is handled by vue3-grid-layout - we just receive the updated layout.
 *
 * This adapter contains all grid-related utility functions that are specific
 * to the V1 layout format. These functions operate on x, y, w, h coordinates.
 */

// ============================================================================
// Internal helper functions (formerly in grid.utils.js)
// ============================================================================

/**
 * Check if two layout items intersect (overlap)
 */
function intersects(a: LayoutPosition, b: LayoutPosition): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Check if a new position intersects with any existing layout items
 */
function intersectsLayout(layout: LayoutItem[], newPosition: LayoutPosition): boolean {
  return layout.some((report) => intersects(report, newPosition));
}

// ============================================================================
// Exported grid utility functions
// ============================================================================

/**
 * Calculate the total number of rows in a layout
 * @returns The maximum row index (y + h) across all items
 */
export function numberOfRows(layout: LayoutItem[]): number {
  return layout.reduce((existingMax, report) => {
    const possibleNewMax = report.y + report.h;
    return possibleNewMax > existingMax ? possibleNewMax : existingMax;
  }, 0);
}

/**
 * Calculate the total number of grid cells
 */
export function numberOfCards(
  layout: LayoutItem[],
  paddingRows = 0,
  cols: number = constants.NUMBER_OF_DASHBOARD_COLS,
): number {
  return numberOfRows(layout) * cols + paddingRows * cols;
}

/**
 * Find an available position in the layout for a new item
 * @returns Position object { x, y, w, h } or null if no space found
 */
export function findPosition(
  layout: LayoutItem[],
  w: number,
  h: number,
  rows: number = numberOfRows(layout),
  cols: number = constants.NUMBER_OF_DASHBOARD_COLS,
): LayoutPosition | null {
  for (let y = 0; y < rows + 1; y += 1) {
    for (let x = 0; x < cols - w + 1; x += 1) {
      const newPosition: LayoutPosition = {
        w,
        h,
        x,
        y,
      };
      if (!intersectsLayout(layout, newPosition)) {
        return newPosition;
      }
    }
  }
  return null;
}

/**
 * Sort layout items by y coordinate, then by x coordinate
 */
export function sortLayout(layoutItemA: LayoutItem, layoutItemB: LayoutItem): number {
  if (layoutItemA.y === layoutItemB.y) {
    return layoutItemA.x - layoutItemB.x;
  }
  return layoutItemA.y - layoutItemB.y;
}

/**
 * Attempts to find where page breaks (or a print row) can be inserted nicely.
 * `maxRowsForPrintRow` is the maximum number of rows that can fit on a page.
 * @returns Array of row indices where page breaks can occur
 */
export function getPrintRowBreaks(layout: LayoutItem[] = []): number[] {
  const maxRows = numberOfRows(layout);
  const printRowBreaks: number[] = [0];
  let lastPrintRowIndex = 0;
  for (let rowIndex = 1; rowIndex <= maxRows; rowIndex += 1) {
    const doAnyLayoutItemsCrossBoundary = layout.some((layoutItem) => {
      const top = layoutItem.y;
      const bottom = layoutItem.y + layoutItem.h;
      return top < rowIndex && rowIndex < bottom;
    });
    const boundaryPrintRowMax = 6;
    const normalPrintRowMax = 8;

    const maxRowsForPrintRow = doAnyLayoutItemsCrossBoundary
      ? boundaryPrintRowMax
      : normalPrintRowMax;
    const rowsSinceLastPrintRow = rowIndex - lastPrintRowIndex;
    const maxRowsForPrintRowReached = rowsSinceLastPrintRow === maxRowsForPrintRow;
    if (!doAnyLayoutItemsCrossBoundary || maxRowsForPrintRowReached) {
      lastPrintRowIndex = rowIndex;
      printRowBreaks.push(rowIndex);
    }
  }
  return printRowBreaks;
}

/**
 * Calculate print rows by grouping layout items into printable sections
 * @returns Nested array of print rows, y-groups, and items
 */
export function calculatePrintRows(layout: LayoutItem[] = []): LayoutItem[][][] {
  const sortedLayout = cloneDeep(layout).sort(sortLayout);
  const printRowBreaks = getPrintRowBreaks(sortedLayout);
  const printRows = printRowBreaks
    .filter((_element, index) => index < printRowBreaks.length - 1) // remove last break index
    .map((breakIndex, index) => {
      const nextBreakIndex = printRowBreaks[index + 1];
      return sortedLayout.filter(
        (layoutItem) => layoutItem.y >= breakIndex && layoutItem.y < nextBreakIndex,
      );
    });

  return printRows.map((printRow) => {
    const groupByY = groupBy(printRow, 'y');
    return Object.values(groupByY);
  });
}

/**
 * Override layout item heights from the report config's oldHeight property
 * Used for backwards compatibility when height values changed
 */
export function overrideLayoutItemHeightFromConstant(layout: LayoutItem[]): LayoutItem[] {
  return ([] as LayoutItem[]).concat(layout).map((layoutItem) => {
    const h = getReportConfigProp(layoutItem.type, 'oldHeight');
    if (isNumber(h)) {
      return {
        ...layoutItem,
        h,
      };
    }
    return layoutItem;
  });
}

// ============================================================================
// Layout Height Adjustment
// ============================================================================

/**
 * Internal overlap check (to avoid circular reference with V1LayoutAdapter object)
 */
function hasAnyOverlapsInternal(layout: LayoutItem[]): boolean {
  for (let i = 0; i < layout.length; i += 1) {
    for (let j = i + 1; j < layout.length; j += 1) {
      const itemA = layout[i];
      const itemB = layout[j];
      const overlaps = !(
        itemA.x >= itemB.x + itemB.w ||
        itemB.x >= itemA.x + itemA.w ||
        itemA.y >= itemB.y + itemB.h ||
        itemB.y >= itemA.y + itemA.h
      );
      if (overlaps) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Check if a layout needs y-adjustment due to height changes
 * @returns True if layout has overlaps after applying new heights
 */
export function needsHeightAdjustment(
  layout: LayoutItem[],
  heightConfigFn: GetHeightConfigFn,
): boolean {
  if (!layout || layout.length === 0) return false;

  const layoutWithNewHeights = layout.map((item) => {
    const config = heightConfigFn(item.type);
    return {
      ...item,
      h: config?.height || item.h,
    };
  });

  // If the layout has overlapping reports after height changes, it needs adjustment
  return layoutWithNewHeights.length > 1 && hasAnyOverlapsInternal(layoutWithNewHeights);
}

/**
 * Adjust layout y-coordinates when report heights have changed
 * This prevents overlaps by shifting items down as needed
 */
export function adjustLayoutForHeightChanges(
  layout: LayoutItem[],
  heightConfigFn: GetHeightConfigFn,
): LayoutItem[] {
  if (!layout || layout.length === 0) return layout;

  if (!needsHeightAdjustment(layout, heightConfigFn)) return layout;

  // Calculate height differences for each report type
  const heightDifferences = new Map<string, number>();
  layout.forEach((item) => {
    const config = heightConfigFn(item.type);
    const height = config?.height;
    const oldHeight = config?.oldHeight;
    if (height && oldHeight && height !== oldHeight) {
      heightDifferences.set(item.type, height - oldHeight);
    }
  });

  // Sort layout by y coordinate
  const sortedLayout = [...layout].sort((a, b) => a.y - b.y);

  // Track cumulative y adjustments per row
  const yAdjustments = new Map<number, number>();

  // Process each item and adjust y based on cumulative adjustments from items above
  const processedLayout = sortedLayout.map((report) => {
    const heightDiff = heightDifferences.get(report.type);

    // Calculate cumulative adjustment from all rows above this item
    let cumulativeAdjustment = 0;
    for (let y = 0; y < report.y; y += 1) {
      cumulativeAdjustment += yAdjustments.get(y) || 0;
    }

    const newReport = { ...report };
    newReport.y = report.y + cumulativeAdjustment;

    // If this item's type has a height difference, record it for items below
    if (heightDiff) {
      const currentRowAdjustment = yAdjustments.get(newReport.y) || 0;
      yAdjustments.set(report.y, currentRowAdjustment + heightDiff);
    }

    return newReport;
  });

  // Return layout in original order
  return layout.map((originalReport) => {
    const originalReportId = originalReport.report_id ?? originalReport.reportId;
    return processedLayout.find((processedItem) => {
      const processedReportId = processedItem.report_id ?? processedItem.reportId;
      return processedReportId === originalReportId;
    })!;
  });
}

// ============================================================================
// Height Configuration Helper
// ============================================================================

/**
 * Get height configuration for a report type.
 * Uses getReportConfigProp to retrieve height values from reports.enum.
 * @param type - Report type string
 * @returns HeightConfig with current and previous height values
 */
export function getHeightConfig(type: string): HeightConfig {
  return {
    height: getReportConfigProp(type, 'height') as number,
    oldHeight: getReportConfigProp(type, 'oldHeight') as number | undefined,
  };
}

// ============================================================================
// V1LayoutAdapter object
// ============================================================================

export const V1LayoutAdapter = {
  // Re-export grid utility functions as adapter methods
  findPosition,
  numberOfRows,
  numberOfCards,
  sortLayout,
  getPrintRowBreaks,
  calculatePrintRows,
  overrideLayoutItemHeightFromConstant,
  // Layout height adjustment
  needsHeightAdjustment,
  adjustLayoutForHeightChanges,
  getHeightConfig,

  /**
   * Add a report to the V1 layout using auto-placement
   */
  addReport(layout: LayoutItem[], { reportId, type }: AddReportOptions): LayoutItem[] {
    const reportConfig = (REPORTS as Record<string, { width: number; height: number }>)[type];
    if (!reportConfig) {
      // Return unchanged if report type is unknown
      return layout;
    }

    const position = findPosition(layout, reportConfig.width, reportConfig.height);
    if (!position) {
      return layout;
    }

    const newLayoutItem: LayoutItem = {
      ...position,
      report_id: reportId,
      type,
    };

    return [...layout, newLayoutItem];
  },

  /**
   * Remove a report from the V1 layout
   */
  removeReport(layout: LayoutItem[], reportId: number): LayoutItem[] {
    return layout.filter((item) => {
      const itemReportId = item.report_id ?? item.reportId;
      return itemReportId !== reportId;
    });
  },

  /**
   * Get a normalized list of reports from the V1 layout
   */
  getReports(layout: LayoutItem[]): NormalizedReportInfo[] {
    if (!Array.isArray(layout)) {
      return [];
    }

    return layout.map((item) => ({
      reportId: (item.report_id ?? item.reportId)!,
      type: item.type,
      position: {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      },
    }));
  },

  /**
   * Find a report in the V1 layout by ID
   */
  findReport(layout: LayoutItem[], reportId: number): LayoutReportInfo | null {
    if (!Array.isArray(layout)) {
      return null;
    }

    const index = layout.findIndex((item) => {
      const itemReportId = item.report_id ?? item.reportId;
      return itemReportId === reportId;
    });

    if (index === -1) {
      return null;
    }

    const item = layout[index];
    return {
      index,
      reportId: (item.report_id ?? item.reportId)!,
      type: item.type,
      position: {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      },
      raw: item,
    };
  },

  /**
   * Serialize the V1 layout for API save
   * Strips out vue3-grid-layout specific properties
   */
  serialize(layout: LayoutItem[]): LayoutItem[] {
    if (!Array.isArray(layout)) {
      return [];
    }

    return layout.map((item) => ({
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      type: item.type,
      report_id: item.report_id ?? item.reportId,
    }));
  },

  /**
   * Validate the V1 layout structure
   */
  validate(layout: LayoutItem[]): LayoutValidationResult {
    const errors: string[] = [];

    if (!Array.isArray(layout)) {
      return { valid: false, errors: ['Layout must be an array'] };
    }

    layout.forEach((item, index) => {
      if (typeof item.x !== 'number') {
        errors.push(`Item ${index}: missing or invalid x coordinate`);
      }
      if (typeof item.y !== 'number') {
        errors.push(`Item ${index}: missing or invalid y coordinate`);
      }
      if (typeof item.w !== 'number' || item.w <= 0) {
        errors.push(`Item ${index}: missing or invalid width`);
      }
      if (typeof item.h !== 'number' || item.h <= 0) {
        errors.push(`Item ${index}: missing or invalid height`);
      }
      if (!item.type) {
        errors.push(`Item ${index}: missing type`);
      }
      if (item.report_id === undefined && item.reportId === undefined) {
        errors.push(`Item ${index}: missing report_id`);
      }
    });

    return { valid: errors.length === 0, errors };
  },

  /**
   * Check if a report exists in the layout
   */
  hasReport(layout: LayoutItem[], reportId: number): boolean {
    return this.findReport(layout, reportId) !== null;
  },

  /**
   * Check if two layout items overlap
   */
  hasOverlap(itemA: LayoutPosition, itemB: LayoutPosition): boolean {
    // Items don't overlap if one is completely to the left, right, above, or below the other
    return !(
      itemA.x >= itemB.x + itemB.w || // A is to the right of B
      itemB.x >= itemA.x + itemA.w || // B is to the right of A
      itemA.y >= itemB.y + itemB.h || // A is below B
      itemB.y >= itemA.y + itemA.h // B is below A
    );
  },

  /**
   * Check if any items in the layout overlap
   */
  hasAnyOverlaps(layout: LayoutItem[]): boolean {
    if (!Array.isArray(layout) || layout.length < 2) {
      return false;
    }

    for (let i = 0; i < layout.length; i += 1) {
      for (let j = i + 1; j < layout.length; j += 1) {
        if (this.hasOverlap(layout[i], layout[j])) {
          return true;
        }
      }
    }
    return false;
  },

  /**
   * Add vue3-grid-layout specific constraints to layout items
   * Adds: i, minW, minH, maxH based on report type configuration
   */
  addGridConstraints(layout: LayoutItem[]): LayoutItem[] {
    if (!Array.isArray(layout)) {
      return [];
    }

    return layout.map((item) => {
      const reportConfig = (REPORTS as Record<string, { width?: number; height?: number }>)?.[
        item.type
      ];
      return {
        ...item,
        i: `${item.reportId ?? item.report_id}`,
        minW: reportConfig?.width ?? 1,
        minH: reportConfig?.height ?? 1,
        maxH: reportConfig?.height ?? 1,
      };
    });
  },

  /**
   * Remove vue3-grid-layout specific properties from layout items
   * Removes: i, moved, minW, minH, maxH
   */
  removeGridConstraints(layout: LayoutItem[]): LayoutItem[] {
    if (!Array.isArray(layout)) {
      return [];
    }

    return layout.map((item) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { i, moved, minW, minH, maxH, ...cleanItem } = item;
      return cleanItem;
    });
  },
};

// Default export for backwards compatibility with grid.utils imports
export default {
  findPosition,
  numberOfRows,
  numberOfCards,
  sortLayout,
  getPrintRowBreaks,
  calculatePrintRows,
};
